import {
  assigneesOf,
  blockersOf,
  canUnlink,
  fmtDate,
  keyLabel,
  LINK_RELATIONS,
  linkEnds,
  linkLabel,
  linkProblem,
  nameList,
  parseKey,
  projectCode,
  type BoardItem,
  type BoardLink,
  type Viewer,
} from "@boem/core/shared";
import { useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, ApiError } from "../api.ts";
import { JumpContext } from "../util.tsx";
import { assigneesTip, RowAvatar } from "./Assign.tsx";
import { Icon } from "./Icons.tsx";
import { ErrorLine, Modal, SecretHint, useSecretCheck } from "./Modal.tsx";
import { dialogRef, itemNo, KindBadge } from "./Structure.tsx";

// Links between items on the card (design 4.8): one row per link in the slot under the sub-items,
// with the sub-item rows' grammar - relation, kind, number, title, assignee - and the same ways to
// look at the other item (▸ here, ⧉ in a panel, ↗ on its board), plus a remove control for whoever
// may remove it; the same 28px icon buttons (Icons.tsx). The rules are the server's (linkProblem,
// canUnlink in shared.ts).

/** How a row numbers the item at the other end: #07 on its project's own board or panel, 01-07 elsewhere, Project 01, #188. */
export function linkNo(l: Pick<BoardLink, "num" | "key" | "projectNum" | "projectNo" | "itemKind">, scope: number | null = null): string {
  const k = l.key ? parseKey(l.key) : null;
  if (l.key && k) return scope != null && scope === l.projectNum ? `#${String(k.seq).padStart(2, "0")}` : l.key;
  if (l.itemKind === "project" && l.projectNo != null) return `Project ${projectCode(l.projectNo)}`;
  return `#${l.num}`;
}

/** The blocked badge's tooltip: what it waits on. */
export const blockedTitle = (it: Pick<BoardItem, "links">, scope: number | null = null) =>
  `Blocked by ${blockersOf(it.links)
    .map((l) => `${linkNo(l, scope)} ${l.title}`)
    .join("; ")}: it waits on ${blockersOf(it.links).length === 1 ? "that" : "those"} being done`;

/**
 * The Links block on a card: every link from this item's side, in reading order (what blocks it
 * first). Nothing when it has none (the link icon in the card's head adds one, the first or the next).
 */
export function Links({
  it,
  viewer,
  scope = null,
  itemOf,
  onRemove,
  onPanel,
  onGoto,
  renderInline,
}: {
  it: BoardItem;
  viewer: Viewer;
  /** the project whose board or panel this is: its items go by #07 there */
  scope?: number | null;
  /** the board's copy of an item (to show its card in place); undefined when the board doesn't have it */
  itemOf?: (num: number) => BoardItem | undefined;
  /** × on a row: shown where canUnlink allows */
  onRemove?: (link: BoardLink) => void;
  onPanel?: (num: number) => void;
  onGoto?: (num: number) => void;
  /** the other item's full card, shown in place; left out inside a card that is itself shown in place */
  renderInline?: (other: BoardItem) => ReactNode;
}) {
  const { jump } = useContext(JumpContext);
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  if (!it.links.length) return null;
  const look = onPanel ?? jump;
  const toggle = (id: string) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <div className="links" aria-label="Links">
      <div className="subitems-head links-head">
        <span className="sub-label">Links &middot; {it.links.length}</span>
      </div>
      <ul>
        {it.links.map((l) => {
          const no = linkNo(l, scope);
          const rel = linkLabel(l.kind, l.dir);
          const other = itemOf?.(l.num);
          const shown = !!renderInline && !!other && open.has(l.id);
          const mayRemove = !!onRemove && canUnlink(viewer, l);
          const made = `${l.by ? `${l.by} linked them` : "Linked"} ${fmtDate(l.at)}${l.note ? `: ${l.note}` : ""}`;
          return (
            <li key={l.id} data-num={l.num} data-status={l.status} data-rel={`${l.kind}-${l.dir}`} className={shown ? "sub-open" : undefined}>
              <div className="sub-row">
                {/* ▸ when its card can open here; else an empty slot, so the rows line up with the sub-items' */}
                {renderInline && other ? (
                  <button
                    type="button"
                    className="sub-act sub-toggle"
                    aria-expanded={shown}
                    aria-label={`${shown ? "Hide" : "Show"} ${no} here`}
                    title={shown ? "Hide it" : "Show it here, in place"}
                    onClick={() => toggle(l.id)}
                  >
                    <Icon name="chevron" />
                  </button>
                ) : (
                  <span className="sub-act sub-gap" aria-hidden="true" />
                )}
                <span className="lk-rel" title={made}>
                  {rel}
                </span>
                <KindBadge kind={l.itemKind} />
                <span className="sub-key" title={l.key ? `${l.key} · #${l.num}` : `#${l.num}`}>
                  {no}
                </span>
                {/* narrow (phone, panel): the title and assignee go to a second line, the controls stay on the first */}
                <span className="lk-break" aria-hidden="true" />
                <button type="button" className="xref sub-title" title={onPanel ? `Open it in a panel: ${l.title}` : `Go to it: ${l.title}`} onClick={() => look(l.num)}>
                  {l.title}
                </button>
                {l.archived && <span className="subitem-asg">archived</span>}
                {/* who has the other item (OEM-285: everyone it's with), as the link says; the card's hover text when this board has it */}
                {l.assignee && (
                  <RowAvatar
                    name={l.assignee}
                    tip={other && assigneesOf(other).length ? assigneesTip(assigneesOf(other)) : `With ${nameList(l.assignees ?? [l.assignee])}`}
                    me={l.assignee === viewer.name}
                    people={(l.assignees ?? [l.assignee]).map((name) => ({ name }))}
                  />
                )}
                <span className="sub-acts">
                  {onPanel && (
                    <button type="button" className="sub-act" aria-label={`Open ${no} in a panel`} title="Open in a panel to the right" onClick={() => onPanel(l.num)}>
                      <Icon name="panel" />
                    </button>
                  )}
                  {onGoto && (
                    <button type="button" className="sub-act" aria-label={`Go to ${no} on its board`} title="Go to it on its board" onClick={() => onGoto(l.num)}>
                      <Icon name="goto" />
                    </button>
                  )}
                  {mayRemove && (
                    <button type="button" className="sub-act lk-x" aria-label={`Remove the link: ${rel} ${no}`} title="Remove this link" onClick={() => onRemove!(l)}>
                      <Icon name="remove" />
                    </button>
                  )}
                </span>
              </div>
              {l.note && <div className="lk-note">{l.note}</div>}
              {shown && other && <div className="sub-inline">{renderInline!(other)}</div>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// -- Add link -------------------------------------------------------------------------------

/** What the item field of Add link names. */
export type LinkTarget =
  /** an item number: on the board (`item`) or not (the server checks it) */
  | { num: number; item?: BoardItem }
  /** a project number the board doesn't know ("01-07" of an archived item): the server resolves it */
  | { ref: string }
  /** title words: the board's items that match, best first */
  | { matches: BoardItem[] }
  | { problem: string };

const MAX_MATCHES = 6;

/**
 * What the Add link item field means: "#188" or "188" (its # number), "01-07" (its project number,
 * current or one it left), or words of a title (matches among `items`). Null while it's empty. Pure.
 */
export function parseLinkTarget(q: string, items: BoardItem[], self: number): LinkTarget | null {
  const s = q.trim();
  if (!s) return null;
  const pick = (num: number): LinkTarget => (num === self ? { problem: "That's this item: pick another one." } : { num, item: items.find((i) => i.num === num) });
  if (/^#?[1-9]\d{0,8}$/.test(s)) return pick(Number(s.replace("#", "")));
  if (/^#?0+\d*$/.test(s)) return { problem: `"${s}" could be more than one item: give its # number or its full project number (01-07).` };
  const k = parseKey(s);
  if (k) {
    const label = keyLabel(k.projectNo, k.seq);
    const hit = items.find((i) => i.key?.label === label) ?? items.find((i) => i.formerKeys.includes(label));
    return hit ? pick(hit.num) : { ref: label };
  }
  const words = s.toLowerCase().split(/\s+/).filter(Boolean);
  const matches = items
    .filter((i) => i.num !== self)
    .filter((i) => {
      const hay = `${i.title} ${i.key?.label ?? ""} #${i.num}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    })
    // open ones first, then the newest
    .sort((a, b) => (a.status === b.status ? b.num - a.num : a.status === "open" ? -1 : 1))
    .slice(0, MAX_MATCHES);
  return { matches };
}

/** How the field shows an item once picked: its project number, else its # number. */
const fieldRef = (it: Pick<BoardItem, "num" | "key">) => it.key?.label ?? `#${it.num}`;

/**
 * Add link: what this item does to the other one (blocks, is blocked by, relates to, duplicates,
 * is duplicated by), the other item ("#188", "01-07", "188" or words of its title), and an optional
 * note on why. Enter adds; with title matches, Enter picks the first.
 */
export function AddLinkModal({
  item,
  items,
  onClose,
  onSaved,
}: {
  item: BoardItem;
  /** the board's items, for the title search */
  items: BoardItem[];
  onClose: () => void;
  /** with what was linked, for a toast: "03-01 blocks 01-07" */
  onSaved: (said: string) => void;
}) {
  const [relIx, setRelIx] = useState(0);
  const [q, setQ] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rel = LINK_RELATIONS[relIx]!;
  const target = useMemo(() => parseLinkTarget(q, items, item.num), [q, items, item.num]);
  const secrets = useSecretCheck(note);
  const me = dialogRef(item);
  const otherNo = !target ? null : "num" in target ? (target.item ? itemNo(target.item) : `#${target.num}`) : "ref" in target ? target.ref : null;
  // the same checks the server makes, on the links this card knows
  const problem = useMemo(() => {
    if (!target) return null;
    if ("problem" in target) return target.problem;
    if (!("num" in target)) return null;
    const [from, to] = rel.dir === "out" ? [item.num, target.num] : [target.num, item.num];
    return linkProblem(from, to, rel.kind, linkEnds(item.num, item.links), (n) => (n === item.num ? me : (otherNo ?? `#${n}`)));
  }, [target, rel, item, me, otherNo]);
  const ready = !!target && ("num" in target || "ref" in target) && !problem && !secrets.length;

  const commit = async () => {
    if (!ready || busy || !target) return;
    setBusy(true);
    setError(null);
    try {
      const to = "num" in target ? target.num : (target as { ref: string }).ref;
      await api.addLink(item.num, { to, kind: rel.kind, dir: rel.dir, ...(note.trim() ? { note: note.trim() } : {}) });
      onSaved(`${me} ${rel.label} ${otherNo ?? to}`);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const choose = (it: BoardItem) => setQ(fieldRef(it));
  // typing the other item is the first thing to do (Modal focuses the relation; this runs after it)
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => field.current?.focus(), []);

  return (
    <Modal onClose={onClose}>
      <h3>Add a link from {me}</h3>
      <p>{item.title}</p>
      <label htmlFor="lk-rel">This item</label>
      <select id="lk-rel" className="search" value={relIx} onChange={(e) => setRelIx(Number(e.target.value))}>
        {LINK_RELATIONS.map((r, i) => (
          <option key={`${r.kind}-${r.dir}`} value={i}>
            {r.label}
          </option>
        ))}
      </select>
      <div className="lk-hint">{rel.hint}</div>
      <label htmlFor="lk-to" style={{ marginTop: 12 }}>
        Which item
      </label>
      <input
        id="lk-to"
        ref={field}
        type="text"
        autoComplete="off"
        value={q}
        placeholder="#188, 01-07, or words from its title"
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          if (target && "matches" in target && target.matches[0]) choose(target.matches[0]);
          else commit();
        }}
      />
      {target && "matches" in target && (
        <ul className="lk-matches" aria-label="Items that match">
          {target.matches.map((m) => (
            <li key={m.num}>
              <button type="button" data-status={m.status} onClick={() => choose(m)}>
                <KindBadge kind={m.kind} />
                <span className="sub-key">{itemNo(m)}</span>
                <span className="lk-m-title">{m.title}</span>
              </button>
            </li>
          ))}
          {!target.matches.length && <li className="lk-none">No item on the board has those words in its title.</li>}
        </ul>
      )}
      {target && ("num" in target || "ref" in target) && !problem && (
        <div className="kind-prev lk-prev">
          {me} <b>{rel.label}</b> {otherNo}
          {"num" in target && target.item ? ` ${target.item.title}` : "num" in target ? " (not on the board: the server checks it)" : " (the server looks it up)"}
          {rel.kind === "blocks" ? (rel.dir === "out" ? ` — ${otherNo} shows as blocked while this one is open.` : ` — this one shows as blocked while ${otherNo} is open.`) : "."}
        </div>
      )}
      {problem && <div className="kind-prev guard">{problem}</div>}
      <label htmlFor="lk-note" style={{ marginTop: 12 }}>
        Why (optional)
      </label>
      <input
        id="lk-note"
        type="text"
        value={note}
        maxLength={500}
        placeholder="e.g. needs the new label endpoint first"
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          commit();
        }}
      />
      <SecretHint findings={secrets} />
      <div className="row">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={!ready || busy} onClick={commit}>
          Add link
        </button>
      </div>
      <ErrorLine error={error} />
      <div className="hint">The link shows on both cards and in both histories. Whoever made it, a lead or an admin can remove it from its row.</div>
    </Modal>
  );
}
